import React, { useMemo, MouseEvent, ReactNode, JSX } from 'react';
import { Button } from 'reactstrap';
import { useTheme } from '@emotion/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faImages } from '@fortawesome/free-solid-svg-icons';
import fallback from '../../../_Translations/generated/picture-gallery.en.t.json';
import Keys from '../../../_Translations/generated/picture-gallery.en.t.json.keys';
import useTranslation from '../../Hooks/useTranslation';
import { ApiImage, ApiImageFromJSON } from '@ibe/api';
import { getImageUrl } from '../../Modules/Camper/externals';
import default404Image from '../UI/Images/Default404Image';

export interface Props {
  images?: ApiImage[];
  onImageClick: (imageIndex: number) => void;
  roundViewLink?: string;
  onShowLayout?: () => void;
  checkProtocol?: boolean;
  hideButtons?: boolean;
  containImage?: boolean;
  numberOfElements?: number;
  externalContent?: ReactNode;
  twoSmallImagesMode?: boolean;
  singleImagePreview?: boolean;
}

const PictureGallery = (props: Props): JSX.Element => {
  const {
    images,
    onImageClick,
    roundViewLink,
    onShowLayout,
    checkProtocol,
    hideButtons,
    containImage,
    numberOfElements,
    externalContent,
    twoSmallImagesMode,
    singleImagePreview
  } = props;
  const { t } = useTranslation('picture-gallery', fallback, Keys);
  const theme = useTheme();

  const shownImages = useMemo(() => {
    return images && images.length > 0 ? [...images] : [ApiImageFromJSON({ url: default404Image })];
  }, [images, theme]);

  return (
    <div className="picture-gallery">
      <div
        className={
          singleImagePreview ? 'picture-gallery__single-image-preview' : 'picture-gallery__large'
        }
        onClick={(): void => onImageClick(0)}
      >
        <img
          className={containImage ? 'picture-gallery__img-contain' : 'picture-gallery__img'}
          src={checkProtocol ? getImageUrl(shownImages[0].url) : shownImages[0].url}
          alt={shownImages[0].altText || 'gallery'}
        />
        <div className="picture-gallery__buttons">
          {roundViewLink && (
            <a
              className="btn btn-secondary mr-2 mb-1 mb-sm-0"
              href={roundViewLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e: MouseEvent<HTMLAnchorElement>): void => e.stopPropagation()}
            >
              {t(Keys.roundView)}
            </a>
          )}
          {onShowLayout && !hideButtons && (
            <Button
              color="secondary"
              onClick={(e): void => {
                e.stopPropagation();
                onShowLayout();
              }}
            >
              {t(Keys.layout)}
            </Button>
          )}
        </div>
        {shownImages.length <= 1 && !hideButtons ? (
          <Button
            onClick={(): void => onImageClick(0)}
            className="picture-gallery__open-slider"
            color="secondary"
          >
            <FontAwesomeIcon className="mr-2" icon={faImages} />
            {t(Keys.showImage)}
          </Button>
        ) : null}
      </div>
      {!singleImagePreview && (
        <div className="picture-gallery__small">
          {externalContent || (
            <>
              {(shownImages.length >= 5 && !twoSmallImagesMode) ||
              (shownImages.length >= 3 && twoSmallImagesMode) ? (
                <>
                  {[...Array(twoSmallImagesMode ? 2 : 4).keys()].map(idx => (
                    <div
                      key={idx}
                      className={`picture-gallery__small__group${
                        twoSmallImagesMode ? ' picture-gallery__small__group--vertical' : ''
                      }`}
                      onClick={(): void => onImageClick(idx + 1)}
                    >
                      <img
                        className={
                          containImage
                            ? 'picture-gallery__small__img-contain'
                            : 'picture-gallery__small__img'
                        }
                        src={
                          checkProtocol
                            ? getImageUrl(shownImages[idx + 1].url)
                            : shownImages[idx + 1].url
                        }
                        alt={shownImages[idx + 1].altText || 'gallery'}
                      />
                    </div>
                  ))}
                </>
              ) : null}
              {shownImages.length < (twoSmallImagesMode ? 3 : 5) && shownImages.length >= 2 ? (
                <div
                  className="picture-gallery__small__single"
                  onClick={(): void => onImageClick(1)}
                >
                  <img
                    className={
                      containImage
                        ? 'picture-gallery__small__img-contain'
                        : 'picture-gallery__small__img'
                    }
                    src={checkProtocol ? getImageUrl(shownImages[1].url) : shownImages[1].url}
                    alt={shownImages[1].altText || 'gallery'}
                  />
                </div>
              ) : null}
            </>
          )}
        </div>
      )}
      {shownImages.length >= 2 && !hideButtons ? (
        <Button
          onClick={(): void => onImageClick(0)}
          className="picture-gallery__open-slider"
          color="secondary"
        >
          <FontAwesomeIcon className="mr-2" icon={faImages} />
          {t(Keys.showAllImages, { numberOfImages: numberOfElements || shownImages.length })}
        </Button>
      ) : null}
    </div>
  );
};

export default PictureGallery;
